import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Plus, Trash2, RefreshCw, Save, Loader2, DollarSign, Clock } from 'lucide-react';
import { toast } from 'sonner';

const CHARGE_TYPES = [
  { value: 'base', label: 'Base Charge' },
  { value: 'weight', label: 'Weight Charge' },
  { value: 'pieces', label: 'Pieces Charge' },
  { value: 'vehicle', label: 'Vehicle Charge' },
  { value: 'fuel_surcharge', label: 'Fuel Surcharge' },
  { value: 'after_hours', label: 'After Hours Charge' },
  { value: 'waiting_shipper', label: 'Waiting Time (Shipper)' },
  { value: 'waiting_consignee', label: 'Waiting Time (Consignee)' },
  { value: 'insurance', label: 'Insurance Charge' },
  { value: 'appointment', label: 'Appointment Charge' },
  { value: 'misc_accessorial', label: 'MISC Accessorial' },
  { value: 'hazmat', label: 'Dangerous Goods/Hazmat' },
  { value: 'signature', label: 'Signature Charge' },
  { value: 'picture_pod', label: 'Picture POD/Document' },
  { value: 'custom', label: 'Custom Charge' },
];

const WAIT_TYPES = ['waiting_shipper', 'waiting_consignee'];

const emptyCharge = () => ({
  charge_id: `chg_${Math.random().toString(36).slice(2, 14)}`,
  charge_type: 'custom', description: '', quantity: 0,
  client_rate: 0, client_charges: 0, override_sales_price: false,
  include_in_fuel: false, gst: 0, hst: 0, pst: 0,
  carrier_rate: 0, carrier_charges: 0, override_carrier_price: false,
  charge_notes: '',
});

function round(n) { return Math.round((n || 0) * 100) / 100; }

function NumField({ label, value, onChange, disabled, prefix, className = '' }) {
  return (
    <div className={className}>
      <label className="text-[10px] text-muted-foreground block mb-1">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">{prefix}</span>}
        <Input
          type="number" step="0.01" value={value}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          disabled={disabled}
          className={`bg-muted border-input text-foreground h-9 text-sm ${prefix ? 'pl-6' : ''} ${disabled ? 'opacity-50' : ''}`}
        />
      </div>
    </div>
  );
}

export default function LoadChargesEditor({ open, onOpenChange, loadId, load }) {
  const { token } = useAuth();
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [billTo, setBillTo] = useState(load?.bill_to || '');
  const [exchangeRate, setExchangeRate] = useState(load?.exchange_rate || 1.0);

  useEffect(() => {
    if (!open || !loadId) return;
    setLoading(true);
    api.get(`/loads/${loadId}/charges`)
      .then(res => setCharges(res.data || []))
      .catch(() => setCharges([]))
      .finally(() => setLoading(false));
  }, [open, loadId, token]);

  const updateCharge = (index, field, value) => {
    setCharges(prev => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      if ((field === 'quantity' || field === 'client_rate') && !next[index].override_sales_price) {
        next[index].client_charges = round(next[index].quantity * next[index].client_rate);
      }
      if ((field === 'quantity' || field === 'carrier_rate') && !next[index].override_carrier_price) {
        next[index].carrier_charges = round(next[index].quantity * next[index].carrier_rate);
      }
      return next;
    });
  };

  const addRow = () => setCharges(prev => [...prev, emptyCharge()]);
  const removeRow = (index) => setCharges(prev => prev.filter((_, i) => i !== index));

  const recalculate = useCallback(() => {
    setCharges(prev => prev.map(c => ({
      ...c,
      client_charges: c.override_sales_price ? c.client_charges : round(c.quantity * c.client_rate),
      carrier_charges: c.override_carrier_price ? c.carrier_charges : round(c.quantity * c.carrier_rate),
    })));
    toast.success('Recalculated');
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post(`/loads/${loadId}/charges/bulk`, { charges });
      toast.success('Charges saved');
      onOpenChange(false);
    } catch {
      toast.error('Failed to save charges');
    } finally {
      setSaving(false);
    }
  };

  const totals = charges.reduce((acc, c) => ({
    clientCharges: acc.clientCharges + (parseFloat(c.client_charges) || 0),
    carrierCharges: acc.carrierCharges + (parseFloat(c.carrier_charges) || 0),
    gst: acc.gst + (parseFloat(c.gst) || 0),
    hst: acc.hst + (parseFloat(c.hst) || 0),
    pst: acc.pst + (parseFloat(c.pst) || 0),
  }), { clientCharges: 0, carrierCharges: 0, gst: 0, hst: 0, pst: 0 });

  const getLabel = (type) => CHARGE_TYPES.find(t => t.value === type)?.label || type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-border text-foreground max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Sticky Header */}
        <div className="px-6 pt-5 pb-4 border-b border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg">
              <DollarSign className="w-5 h-5 text-brand-accent" /> Load Charges
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-end gap-4 mt-4">
            <div className="flex-1">
              <label className="text-xs text-muted-foreground block mb-1">Bill To</label>
              <Input value={billTo} onChange={e => setBillTo(e.target.value)}
                placeholder="Customer name" className="bg-muted border-input text-foreground h-9" />
            </div>
            <div className="w-32">
              <label className="text-xs text-muted-foreground block mb-1">Exchange Rate</label>
              <Input type="number" step="0.01" value={exchangeRate} onChange={e => setExchangeRate(e.target.value)}
                className="bg-muted border-input text-foreground h-9" />
            </div>
            <Button variant="outline" size="sm" onClick={recalculate} className="border-input text-muted-foreground h-9 gap-1.5">
              <RefreshCw className="w-3.5 h-3.5" /> Re-Calculate
            </Button>
            <Button size="sm" onClick={addRow} className="bg-brand-accent text-brand-accent-foreground h-9 gap-1.5 font-bold">
              <Plus className="w-3.5 h-3.5" /> Add Charge
            </Button>
          </div>
        </div>

        {/* Scrollable charge cards */}
        <ScrollArea className="flex-1 px-6 py-4">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : charges.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-30" />
              <p>No charges yet. Click "Add Charge" to start.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {charges.map((c, i) => {
                const isWait = WAIT_TYPES.includes(c.charge_type);
                return (
                  <div key={c.charge_id || i}
                    className="bg-muted/50 border border-input/50 rounded-xl p-4 hover:border-input transition-colors">
                    {/* Row 1: Type + Client side */}
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-3">
                          <select value={c.charge_type} onChange={e => updateCharge(i, 'charge_type', e.target.value)}
                            className="bg-muted border border-input text-foreground text-sm rounded-lg px-3 py-2 font-medium w-64">
                            {CHARGE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                          {isWait && (
                            <div className="flex items-center gap-1.5 text-amber-400 text-xs">
                              <Clock className="w-3.5 h-3.5" />
                              <span>Wait time charge</span>
                            </div>
                          )}
                          <button onClick={() => removeRow(i)}
                            className="ml-auto text-muted-foreground hover:text-red-400 transition-colors p-1">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>

                        {/* Client pricing row */}
                        <div className="grid grid-cols-6 gap-3 mb-3">
                          <NumField label={isWait ? "Hours" : "Quantity"} value={c.quantity}
                            onChange={v => updateCharge(i, 'quantity', v)} />
                          <NumField label="Client Rate" value={c.client_rate} prefix="$"
                            onChange={v => updateCharge(i, 'client_rate', v)} />
                          <NumField label="Client Charges" value={c.client_charges} prefix="$"
                            onChange={v => updateCharge(i, 'client_charges', v)}
                            disabled={!c.override_sales_price} />
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Override</label>
                            <div className="flex items-center gap-2 h-9">
                              <Checkbox checked={c.override_sales_price}
                                onCheckedChange={v => updateCharge(i, 'override_sales_price', v)}
                                className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent" />
                              <span className="text-[10px] text-muted-foreground">Manual</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Incl. Fuel</label>
                            <div className="flex items-center gap-2 h-9">
                              <Checkbox checked={c.include_in_fuel}
                                onCheckedChange={v => updateCharge(i, 'include_in_fuel', v)}
                                className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent" />
                              <span className="text-[10px] text-muted-foreground">Yes</span>
                            </div>
                          </div>
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Total</label>
                            <div className="h-9 flex items-center">
                              <span className="text-sm font-bold text-brand-accent">
                                ${(parseFloat(c.client_charges) || 0).toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Carrier + Tax row */}
                        <div className="grid grid-cols-6 gap-3">
                          <NumField label="Carrier Rate" value={c.carrier_rate} prefix="$"
                            onChange={v => updateCharge(i, 'carrier_rate', v)} />
                          <NumField label="Carrier Charges" value={c.carrier_charges} prefix="$"
                            onChange={v => updateCharge(i, 'carrier_charges', v)}
                            disabled={!c.override_carrier_price} />
                          <div>
                            <label className="text-[10px] text-muted-foreground block mb-1">Override</label>
                            <div className="flex items-center gap-2 h-9">
                              <Checkbox checked={c.override_carrier_price}
                                onCheckedChange={v => updateCharge(i, 'override_carrier_price', v)}
                                className="border-input data-[state=checked]:bg-brand-accent data-[state=checked]:border-brand-accent" />
                              <span className="text-[10px] text-muted-foreground">Manual</span>
                            </div>
                          </div>
                          <NumField label="GST" value={c.gst} prefix="$"
                            onChange={v => updateCharge(i, 'gst', v)} />
                          <NumField label="HST" value={c.hst} prefix="$"
                            onChange={v => updateCharge(i, 'hst', v)} />
                          <NumField label="PST" value={c.pst} prefix="$"
                            onChange={v => updateCharge(i, 'pst', v)} />
                        </div>

                        {/* Notes */}
                        <div className="mt-3">
                          <Input value={c.charge_notes || ''} onChange={e => updateCharge(i, 'charge_notes', e.target.value)}
                            placeholder="Charge notes (optional)"
                            className="bg-muted/50 border-input/50 text-foreground h-8 text-xs" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>

        {/* Sticky Footer Totals */}
        <div className="px-6 py-4 border-t border-border bg-surface">
          <div className="flex items-center justify-between">
            <div className="grid grid-cols-6 gap-6 text-sm">
              <div>
                <p className="text-[10px] text-muted-foreground">Client Total</p>
                <p className="text-foreground font-bold">${totals.clientCharges.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Carrier Total</p>
                <p className="text-foreground font-bold">${totals.carrierCharges.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">GST</p>
                <p className="text-muted-foreground">${totals.gst.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">HST</p>
                <p className="text-muted-foreground">${totals.hst.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">PST</p>
                <p className="text-muted-foreground">${totals.pst.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground">Grand Total</p>
                <p className="text-brand-accent font-bold text-lg">${(totals.clientCharges + totals.gst + totals.hst + totals.pst).toFixed(2)}</p>
              </div>
            </div>
            <Button onClick={handleSave} disabled={saving} className="bg-brand-accent text-brand-accent-foreground font-bold h-10 px-6 gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving...' : 'Save Charges'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
